/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { SagaIterator } from 'redux-saga';
import { all, call, select } from 'redux-saga/effects';
import {
  Channel,
  FileFormat,
  translate,
  WatchOptionTv,
  WatchOption,
  Vod,
  Stream,
  ProductPrice,
  WatchOptionVod,
  AssetOptionType,
  WatchOptionType,
  Program,
  Seconds,
  TimeUtils,
  TvAsset,
  Asset,
  ProductPriceStatus,
  ContentTagsUtils,
  TvAssetUtils,
  RelativeTime,
  OptionUtils,
  AssetBookmark,
  BookmarkUtils,
  ContentProps,
  AssetUtils,
  Ducks,
} from '@wit/vtv-x-foundation-lib';
import { Selectors as PlayableChannelsSelectors } from '../../epg/playableChannels';

const fileFormatOrder = [FileFormat.MAIN_UHD, FileFormat.MAIN_HD, FileFormat.MAIN];

function getVodOptionTitle(format: FileFormat): string {
  switch (format) {
    case FileFormat.MAIN_UHD:
      return translate['pi.options.watch.uhd'];
    case FileFormat.MAIN_HD:
      return translate['pi.options.watch.hd'];
    default:
      return translate['pi.options.watch'];
  }
}

function sortWatchOptions(watchOptions: WatchOptionTv[] | WatchOption[]): WatchOption[] {
  return watchOptions.sort((o1, o2) => {
    const o1StreamFormatType = o1.channel?.streams[0].fileFormat;
    const o2StreamFormatType = o2.channel?.streams[0].fileFormat;

    if (o1StreamFormatType === undefined || o2StreamFormatType === undefined) {
      return 0;
    }

    return fileFormatOrder.indexOf(o1StreamFormatType) - fileFormatOrder.indexOf(o2StreamFormatType);
  });
}

function buildVodOption(
  asset: Vod,
  stream: Stream,
  streamPrice: ProductPrice,
  channels: Channel[],
  progress: number | undefined
): WatchOptionVod {
  return {
    key: `${AssetOptionType.WATCH}-${WatchOptionType.VOD}-${asset.id}-${stream.id}`,
    type: AssetOptionType.WATCH,
    title: getVodOptionTitle(stream.fileFormat),
    asset,
    label: '',
    watchOptionType: WatchOptionType.VOD,
    stream,
    price: streamPrice,
    startDate: streamPrice.startDate,
    channel: AssetUtils.getAssetRelatedChannel(asset, channels),
    progress,
  };
}

function buildTvLiveOption(asset: Program, channel: Channel): WatchOptionTv {
  const progressTime: Seconds = TimeUtils.nowUnixEpoch() - TimeUtils.toUnixEpoch(asset.startDate);

  return {
    key: `${AssetOptionType.WATCH}-${WatchOptionType.TV_LIVE}-${channel.id}-${asset.id}`,
    type: AssetOptionType.WATCH,
    title: channel.title,
    asset,
    progress: AssetUtils.getProgressInPercentage(progressTime, TimeUtils.minutesToSeconds(asset.durationValue)),
    label: '',
    watchOptionType: WatchOptionType.TV_LIVE,
    channel,
  };
}

function buildTvPastOption(asset: TvAsset, channel: Channel, progress: number | undefined): WatchOptionTv {
  return {
    key: `${AssetOptionType.WATCH}-${WatchOptionType.TV_PAST}-${channel.id}-${asset.id}`,
    type: AssetOptionType.WATCH,
    title: channel.title,
    asset,
    label: '',
    watchOptionType: WatchOptionType.TV_PAST,
    channel,
    progress,
  };
}

function getVodsOptions(
  clones: Asset[],
  channels: Channel[],
  vodsFilesPrices: ProductPrice[],
  progress: number | undefined
): WatchOption[] {
  const options: WatchOptionVod[] = [];

  // the available prices belong to the allowed vods
  const allowedFilesPrices = vodsFilesPrices.filter(
    (price) => price.status === ProductPriceStatus.FREE || price.status === ProductPriceStatus.PURCHASED
  );

  if (allowedFilesPrices.length === 0) {
    return options;
  }

  const vodAssets = clones.filter(
    (clone) => AssetUtils.isVod(clone) && ContentTagsUtils.isPlaybackAllowedForVodAsset(clone)
  );

  let filePrice;
  vodAssets.forEach((vodAsset) => {
    vodAsset.streams.forEach((stream) => {
      filePrice = allowedFilesPrices.find((price) => price.fileId === stream.id);
      if (filePrice) {
        options.push(buildVodOption(vodAsset as Vod, stream, filePrice, channels, progress));
      }
    });
  });

  let index1;
  let index2;
  const sortedOptions = options.sort((op1, op2) => {
    if (op1.stream.fileFormat !== op2.stream.fileFormat) {
      index1 = fileFormatOrder.indexOf(op1.stream.fileFormat);
      index2 = fileFormatOrder.indexOf(op2.stream.fileFormat);
      // invalid formats are considered as if they are at the end of the array
      index1 = index1 < 0 ? options.length : index1;
      index2 = index2 < 0 ? options.length : index2;

      return index1 - index2;
    }

    return TimeUtils.compare(op2.startDate || TimeUtils.fromUnixEpoch(0), op1.startDate || TimeUtils.fromUnixEpoch(0));
  });

  // To avoid duplications caused by sd/hd/npvr file types using the same file, if the file has an id don't add it twice
  // also if the file format is the same, show only the first option
  const res = sortedOptions.reduce((acc: WatchOptionVod[], option) => {
    if (
      acc.findIndex(
        (accOption) =>
          accOption.stream.id === option.stream.id || accOption.stream.fileFormat === option.stream.fileFormat
      ) === -1
    ) {
      acc.push(option);
    }
    return acc;
  }, []);

  return res;
}

function getTvPastOptions(pastAssets: TvAsset[], channels: Channel[], progress: number | undefined): WatchOption[] {
  const pastAssetsAgg = OptionUtils.aggregateByChannel(pastAssets);

  const options: WatchOptionTv[] = [];

  let relatedChannel;
  pastAssetsAgg.forEach((pastAsset) => {
    relatedChannel = channels.find((channel) => channel.epgId === pastAsset.channelEpgId);

    if (relatedChannel) {
      options.push(buildTvPastOption(pastAsset, relatedChannel, progress));
    }
  });

  return sortWatchOptions(options);
}

function getRecordingsOptions(clones: Asset[], channels: Channel[], progress: number | undefined): WatchOption[] {
  const pastAssets = clones.filter(
    (clone) =>
      AssetUtils.isRecording(clone) &&
      ContentTagsUtils.isPlaybackAllowedForRecording(clone) &&
      TvAssetUtils.getRelativeTime(clone) !== RelativeTime.FUTURE
  ) as TvAsset[];

  if (pastAssets.length === 0) {
    return [];
  }

  return getTvPastOptions(pastAssets, channels, progress);
}

function getCatchupOptions(clones: Asset[], channels: Channel[], progress: number | undefined): WatchOption[] {
  const pastAssets = clones.filter(
    (clone) =>
      AssetUtils.isEpg(clone) &&
      ContentTagsUtils.isPlaybackAllowedForCatchupAsset(clone) &&
      TvAssetUtils.getRelativeTime(clone) !== RelativeTime.FUTURE
  ) as TvAsset[];

  if (pastAssets.length === 0) {
    return [];
  }

  return getTvPastOptions(pastAssets, channels, progress);
}

function getTvLiveOptions(clones: Asset[], channels: Channel[]): WatchOption[] {
  const liveAssets = clones.filter(
    (clone) =>
      AssetUtils.isEpg(clone) &&
      TvAssetUtils.getRelativeTime(clone) === RelativeTime.LIVE &&
      ContentTagsUtils.isPlaybackAllowedForLiveAsset(clone)
  ) as Program[];

  if (liveAssets.length === 0) {
    return [];
  }

  const options: WatchOption[] = [];

  let relatedChannel;
  liveAssets.forEach((liveAsset) => {
    relatedChannel = channels.find((channel) => channel.epgId === liveAsset.channelEpgId);

    if (relatedChannel) {
      options.push(buildTvLiveOption(liveAsset, relatedChannel));
    }
  });

  return sortWatchOptions(options);
}

function* getOptions(
  representativeAsset: Asset,
  clones: Asset[],
  vodsFilesPrices: ProductPrice[]
): SagaIterator<WatchOption[]> {
  const channels: Channel[] = yield select(PlayableChannelsSelectors.getChannels) || [];

  const clonesBookmars: AssetBookmark[] = yield select(Ducks.BookmarksCache.Selectors.getAssetsBookmarks, clones);

  // all clones are ContentProps: vod, program or recording
  const progress = BookmarkUtils.getAggregatedProgressBookmarks(representativeAsset as ContentProps, clonesBookmars);

  const [vodsOptions, recordingsOptions, catchupOptions, liveOptions]: WatchOption[][] = yield all([
    call(getVodsOptions, clones, channels, vodsFilesPrices, progress),
    call(getRecordingsOptions, clones, channels, progress),
    call(getCatchupOptions, clones, channels, progress),
    call(getTvLiveOptions, clones, channels),
  ]);

  const results = [...vodsOptions, ...recordingsOptions, ...catchupOptions, ...liveOptions];

  return results;
}

export default {
  getOptions,
};
